import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useStandardUserRegistration } from '@src/state/SURegistration';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { useTIN } from '@src/state/taxInformation';
import { useEditing } from '@src/state/editing/hooks';
import { useUser } from '@src/state/user';
import { event as adobeTaggingEvent, event } from '@uhg-abyss/web/tools/event';
import { getOrgType } from '@src/common/helpers';
import { useOnloading } from '@src/state/landing';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  paddingTop: '35px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const TipLabel = styled('span', {
  fontWeight: 400,
  fontSize: '80%',
  width: '100%',
  fontStyle: 'italic',
  color: '$gray8',
  display: 'inline-block',
  marginTop: '5px',
  lineHeight: '14px',
});

const InvalidSelectionError = styled('span', {
  fontWeight: 600,
  fontSize: '80%',
  width: '100%',
  color: 'red',
  display: 'inline-block',
  marginTop: '5px',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '10px',
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  float: 'right',
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const Separator = styled('hr', {
  overflow: 'visible',
  padding: 0,
  border: 'none',
  borderTop: 'solid thin #c9ced3',
  color: 'rgb(168, 167, 167)',
  textAlign: 'center',
  marginTop: '30px',
  marginBottom: '30px',
  width: '70%',
  '&:after': {
    content: "'OR'",
    display: 'inline-block',
    position: 'relative',
    fontWeight: 900,
    top: '-1.2em',
    fontSize: '12px',
    padding: '0 0.5em',
    background: 'white',
  },
});

export const AdminSelection = ({ handleNext, handleBack }) => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const form = useForm();
  const { selectedAdmin, unknownPAA } = useStandardUserRegistration.state();
  const { isEditing } = useEditing.state();
  const [invalidSelection, setInvalidSelection] = useState(false);
  const { TINNumber, TINInfo } = useTIN.state();
  const { setUnknownPAA, selectAdmin } = useStandardUserRegistration.actions();
  const paaInfo = TINInfo ? TINInfo.paaInfo : null;
  const { userUUID } = useUser.state();
  const { currentFlow } = useOnloading.state();

  useEffect(() => {
    const adminToShow =
      !unknownPAA && selectedAdmin
        ? {
            label: `${selectedAdmin.firstName} ${selectedAdmin.lastName[0]}`,
            value: selectedAdmin.id,
          }
        : null;
    form.setValue('SUAdminSelect.selectedAdmin', adminToShow);
    form.setValue('SUAdminSelect.PAAUnknown', unknownPAA);

    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'tin registration';
      const section3 = 'select access administrator';
      const pageName = `digitalsecurity:${section1}:${orgType}:${section3}`;

      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2: orgType,
        section3,
      });
    }
  }, []);

  const onNext = () => {
    if (selectedAdmin) {
      handleNext();
    } else {
      setInvalidSelection(true);
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const fields = ['Admin Selection Dropdown'];
        event('FORM_VALIDATION_ERROR', { fields });
      }
    }
  };

  const onAdminSelected = (e) => {
    form.setValue('SUAdminSelect.PAAUnknown', false);
    setInvalidSelection(false);
    const admin = paaInfo.filter((paa) => {
      return e === paa.id;
    })[0];
    selectAdmin(admin);
  };

  const onUnknownAdminChecked = (e) => {
    form.setValue('SUAdminSelect.selectedAdmin', null);
    setInvalidSelection(false);

    let primaryAdmin = null;
    for (let i = 0; i < paaInfo.length; i++) {
      if (paaInfo[i].primary) {
        primaryAdmin = paaInfo[i];
        break;
      }
    }

    setUnknownPAA(e.target.checked);

    if (e.target.checked) {
      selectAdmin(primaryAdmin || paaInfo[0]);
    } else {
      selectAdmin(null);
    }
  };

  const getAdmins = () => {
    if (paaInfo && paaInfo.length > 1) {
      return paaInfo.map((paa) => {
        return {
          label: `${paa.firstName} ${paa.lastName[0]}`,
          value: `${paa.id}`,
        };
      });
    }
    return [];
  };

  return (
    <React.Fragment>
      <MainContainer>
        <BoxHeader>
          <BoxHeaderText>Select an administrator.</BoxHeaderText>
        </BoxHeader>
        <Description>
          Please select an administrator from the list below to approve your
          access to UnitedHealthcare Provider Portal.
        </Description>
        <FormProvider state={form}>
          <div style={{ width: '45%' }}>
            <SelectInput
              data-testid="admin-dropdown"
              label="Administrator"
              model="SUAdminSelect.admin"
              isClearable
              isSearchable
              value={selectedAdmin && selectedAdmin.id}
              options={getAdmins()}
              onChange={onAdminSelected}
              validators={{ required: !unknownPAA }}
              isDisabled={!!unknownPAA}
              css={{
                'abyss-select-input-label': {
                  fontWeight: 'normal',
                },
              }}
            />
          </div>
          <TipLabel>You can start typing to search for a name faster.</TipLabel>
        </FormProvider>
        <Separator />
        <FormProvider state={form}>
          <Checkbox
            size="md"
            model="SUAdminSelect.PAAUnknown"
            label="I don't know who the administrator is."
            isChecked={!unknownPAA}
            onChange={onUnknownAdminChecked}
          />
        </FormProvider>
        {invalidSelection ? (
          <InvalidSelectionError>
            Please select an administrator or indicate that you don't know who
            the administrator is.
          </InvalidSelectionError>
        ) : null}
      </MainContainer>
      <Flex justify="space-between">
        <div>
          {!isEditing && (
            <BackButton
              onClick={() => {
                handleBack();
              }}
            >
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          )}
        </div>
        <div>
          <NextButton onClick={onNext}>
            <NextButtonText>{isEditing ? 'Save' : 'Next'}</NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </div>
      </Flex>
    </React.Fragment>
  );
};

AdminSelection.propTypes = {
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
};
